import { useEffect, useMemo, useRef, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { scheduleTodoReminders, showSystemTodoReminder } from '../capabilities/todo-reminders';
import { scheduleCourseReminders, type CourseReminderOccurrence } from '../capabilities/course-reminders';
import { showSystemReminder } from '../capabilities/todo-reminders';
import { useSync } from '../lib/sync-context';
import { selectPayloads } from '../lib/workspace-records';
import type { AcademicCourseDocument, AcademicTimetableDocument, TodoDocument } from '../types/sync';

type LearningReminderNotice = { kind: 'todo'; todo: TodoDocument } | { kind: 'course'; occurrence: CourseReminderOccurrence };

export function TodoReminderBridge() {
  const { snapshot } = useSync();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<LearningReminderNotice>();
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const todos = useMemo(
    () => selectPayloads<TodoDocument>(snapshot.records, 'tasks.todo'),
    [snapshot.records],
  );
  const courses = useMemo(
    () => selectPayloads<AcademicCourseDocument>(snapshot.records, 'academics.course'),
    [snapshot.records],
  );
  const timetable = useMemo(
    () => selectPayloads<AcademicTimetableDocument>(snapshot.records, 'academics.timetable')[0],
    [snapshot.records],
  );

  useEffect(() => scheduleTodoReminders(todos, todo => {
    setNotice({ kind: 'todo', todo });
    void showSystemTodoReminder(todo);
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => setNotice(undefined), 10_000);
  }), [todos]);

  useEffect(() => scheduleCourseReminders(courses, timetable, occurrence => {
    setNotice({ kind: 'course', occurrence });
    void showSystemReminder({
      title: `${occurrence.course.name}即将上课`,
      body: `${occurrence.reminderMinutesBefore} 分钟后 · ${occurrence.schedule.location || `第 ${occurrence.schedule.startSlot}–${occurrence.schedule.endSlot} 节`}`,
      tag: `aulivis-course-${occurrence.schedule.id}-${occurrence.day.toISOString().slice(0, 10)}`,
      url: `/courses?view=timetable&course=${encodeURIComponent(occurrence.course.id)}`,
    });
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => setNotice(undefined), 10_000);
  }), [courses, timetable]);

  useEffect(() => () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
  }, []);

  if (!notice) return null;
  const openTodo = () => {
    setNotice(undefined);
    navigate(notice.kind === 'todo'
      ? `/plan?tab=todos&todo=${encodeURIComponent(notice.todo.id)}`
      : `/courses?view=timetable&course=${encodeURIComponent(notice.occurrence.course.id)}`);
  };
  const title = notice.kind === 'todo' ? '待办到时间了' : `${notice.occurrence.course.name}即将上课`;
  const detail = notice.kind === 'todo' ? notice.todo.title : `${notice.occurrence.reminderMinutesBefore} 分钟后 · ${notice.occurrence.schedule.location || '查看课表'}`;
  return (
    <aside className="todo-reminder-toast" role="status" aria-live="polite">
      <button className="todo-reminder-content" onClick={openTodo}>
        <span><BellRing /></span>
        <span><strong>{title}</strong><small>{detail}</small></span>
      </button>
      <button className="todo-reminder-close" onClick={() => setNotice(undefined)} aria-label="关闭提醒"><X /></button>
    </aside>
  );
}
